import React, { createContext, useContext, useState, useEffect } from 'react';
import * as authUtils from '../utils/auth';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const hasStoredToken = typeof window !== 'undefined' && authUtils.isAuthenticated();
  const hasOAuthParams = typeof window !== 'undefined' && 
    new URLSearchParams(window.location.search).has('access_token') &&
    new URLSearchParams(window.location.search).has('refresh_token');
  const [user, setUser] = useState(() => (hasStoredToken ? authUtils.getUser() : null));
  const [loading, setLoading] = useState(() => (hasStoredToken && !authUtils.getUser()) || hasOAuthParams);

  const expireForInactivity = () => {
    window.dispatchEvent(new CustomEvent('craftmyparty:session-expiring', {
      detail: { reason: 'inactive' }
    }));
    authUtils.clearAuth();
    setUser(null);
    window.dispatchEvent(new CustomEvent('craftmyparty:session-expired', {
      detail: { reason: 'inactive' }
    }));
  };

  const refreshUser = async ({ preserveOnFailure = false, force = false } = {}) => {
    const currentUser = await authUtils.getCurrentUser({ force });
    if (currentUser) {
      setUser(currentUser);
      return currentUser;
    }
    if (!preserveOnFailure) {
      setUser(null);
    }
    return null;
  };

  // Check for existing auth on mount
  useEffect(() => {
    const initAuth = async () => {
      try {
        // Detect OAuth callback tokens in URL
        if (typeof window !== 'undefined') {
          const params = new URLSearchParams(window.location.search);
          const urlAccessToken = params.get('access_token');
          const urlRefreshToken = params.get('refresh_token');
          if (urlAccessToken && urlRefreshToken) {
            // Write them to localStorage immediately
            authUtils.setAuthTokens(urlAccessToken, urlRefreshToken, null);
            
            // Get fresh user record from the backend using the tokens
            const currentUser = await authUtils.getCurrentUser({ force: true });
            if (currentUser) {
              setUser(currentUser);
              // Save fully populated profile
              authUtils.setAuthTokens(urlAccessToken, urlRefreshToken, currentUser);
            } else {
              authUtils.clearAuth();
            }

            // Cleanup query parameters to prevent leakage in URL and browser history
            const cleanSearch = new URLSearchParams(window.location.search);
            cleanSearch.delete('access_token');
            cleanSearch.delete('refresh_token');
            cleanSearch.delete('token_type');
            
            const searchStr = cleanSearch.toString();
            const joiner = searchStr ? `?${searchStr}` : '';
            const newPath = window.location.pathname === '/auth/callback' ? '/' : window.location.pathname;
            window.history.replaceState({}, '', `${newPath}${joiner}`);
            setLoading(false);
            return;
          }
        }

        // First, check if backend URL has changed (e.g., switching from dev to prod)
        // This will clear tokens if they're for a different backend
        const backendUrlValid = authUtils.validateBackendUrl();
        if (!backendUrlValid) {
          // Tokens were cleared due to backend URL mismatch
          setLoading(false);
          return;
        }
        
        if (authUtils.isAuthenticated()) {
          if (authUtils.isSessionInactive()) {
            expireForInactivity();
            setLoading(false);
            return;
          }

          const cached = authUtils.getUser();
          if (cached) {
            setUser(cached);
            setLoading(false);
            authUtils.getCurrentUser()
              .then((currentUser) => {
                if (currentUser) setUser(currentUser);
              })
              .catch(() => {});
            return;
          }

          const currentUser = await authUtils.getCurrentUser({ force: true });
          if (currentUser) {
            setUser(currentUser);
          } else {
            authUtils.clearAuth();
            setUser(null);
          }
        } else {
          setUser(null);
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        // Don't clear auth on network errors - might just be backend down
        // Only clear if it's an auth error
        if (error.message && error.message.includes('401')) {
          authUtils.clearAuth();
        }
      } finally {
        setLoading(false);
      }
    };

    initAuth();

    let lastForegroundCheck = 0;
    const handleForegroundResume = async () => {
      if (!authUtils.isAuthenticated()) return;

      const now = Date.now();
      // Throttle active backend validation to once every 10 seconds to avoid API spam
      if (now - lastForegroundCheck < 10000) return;
      lastForegroundCheck = now;

      try {
        if (authUtils.isSessionInactive()) {
          expireForInactivity();
          return;
        }
        authUtils.touchSessionActivity();

        // Perform active backend check of token validity
        const currentUser = await authUtils.getCurrentUser({ force: true });
        if (!currentUser) {
          // Token is invalid/expired on backend! Trigger session expiration.
          expireForInactivity();
        }
      } catch (error) {
        // If the error is a 401/credentials failure, log out cleanly.
        // Network/offline errors should be ignored so we don't log out offline users.
        if (error.response && error.response.status === 401) {
          expireForInactivity();
        }
      }
    };

    // Re-validate session whenever the user returns to this tab after being away
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        handleForegroundResume();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    if (typeof window !== 'undefined') {
      window.addEventListener('focus', handleForegroundResume);
      window.addEventListener('pageshow', handleForegroundResume);
    }

    const inactivityWatchdog = window.setInterval(() => {
      if (!authUtils.isAuthenticated()) return;
      if (authUtils.isSessionInactive()) {
        expireForInactivity();
      }
    }, 30 * 1000);

    const handleActivity = () => {
      const status = authUtils.touchSessionActivity();
      if (status === 'expired') {
        expireForInactivity();
      }
    };

    const activityEvents = ['mousedown', 'keydown', 'touchstart', 'scroll'];
    activityEvents.forEach((eventName) => {
      window.addEventListener(eventName, handleActivity, { passive: true });
    });

    // Listen for storage changes (multi-tab sync)
    let lastEventDispatched = 0;
    const handleStorageChange = (e) => {
      // Check for logout flag
      if (e.key === 'craftmyparty_logout_flag' && e.newValue) {
        // Logout was triggered in another tab
        setUser(null);
        setLoading(false);

        const now = Date.now();
        if (now - lastEventDispatched > 1000) {
          lastEventDispatched = now;
          window.dispatchEvent(new CustomEvent('craftmyparty:multi-tab-logout'));
        }
        return;
      }
      
      // Check for auth token changes
      if (e.key === 'craftmyparty_access_token' || 
          e.key === 'craftmyparty_refresh_token' || 
          e.key === 'craftmyparty_user') {
        // Auth state changed in another tab
        if (e.newValue === null) {
          // Tokens were removed (logout in another tab)
          setUser(null);
          setLoading(false);

          const now = Date.now();
          if (now - lastEventDispatched > 1000) {
            lastEventDispatched = now;
            window.dispatchEvent(new CustomEvent('craftmyparty:multi-tab-logout'));
          }
        } else {
          // Tokens were added/updated (login in another tab)
          initAuth();
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.clearInterval(inactivityWatchdog);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (typeof window !== 'undefined') {
        window.removeEventListener('focus', handleForegroundResume);
        window.removeEventListener('pageshow', handleForegroundResume);
      }
      window.removeEventListener('storage', handleStorageChange);
      activityEvents.forEach((eventName) => {
        window.removeEventListener(eventName, handleActivity);
      });
    };
  }, []);

  const login = async (email, password) => {
    try {
      const data = await authUtils.login(email, password);
      setUser(data.user);
      return data;
    } catch (error) {
      throw error;
    }
  };

  const register = async (email, password, name) => {
    try {
      const data = await authUtils.register(email, password, name);
      setUser(data.user);
      return data;
    } catch (error) {
      throw error;
    }
  };

  const logout = () => {
    authUtils.logout();
    setUser(null);
  };

  /**
   * Call this from any API error handler when you get a 401/credentials error.
   * Tries to silently refresh the token first; if that fails, logs the user out
   * and fires a custom event so the UI can open the auth modal.
   */
  const handleExpiredSession = async () => {
    try {
      await authUtils.refreshAccessToken();
      // Refresh succeeded — re-fetch user so context stays in sync
      const currentUser = await refreshUser({ force: true });
      if (currentUser) {
        setUser(currentUser);
        return 'refreshed';
      }
    } catch (_) {
      // Refresh failed — hard logout
    }
    authUtils.clearAuth();
    setUser(null);
    // Notify the UI (App.js listens for this to open the auth modal)
    window.dispatchEvent(new CustomEvent('craftmyparty:session-expired'));
    return 'logged-out';
  };

  const value = {
    user,
    loading,
    isAuthenticated: !!user && authUtils.isAuthenticated(),
    login,
    register,
    logout,
    refreshToken: authUtils.refreshAccessToken,
    refreshUser,
    handleExpiredSession,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
